"use client"

import { LeasingCarDto, SaleCarDto } from "api"
import { useCallback } from "react"
import {
  PreviouslyViewedCar,
  transformCarDataForPreviouslyViewed,
  transformLeasingCarDataForPreviouslyViewed,
} from "./carDataTransform"
import { useLocalStorage } from "./useLocalStorage"

const STORAGE_KEY = "eurodrive_previously_viewed_cars"
const MAX_CARS = 10 // Maximum number of cars to store

// Type utility to determine car type
type CarType = "Sale" | "Leasing"
type CarDtoType<T extends CarType> = T extends "Sale"
  ? SaleCarDto
  : LeasingCarDto

export function usePreviouslyViewed<T extends CarType = "Sale">({
  brands,
}: {
  brands: {
    image: string
    title: string
    logo: string
    url: string
  }[]
}) {
  const [viewedCars, setViewedCars] = useLocalStorage<PreviouslyViewedCar[]>(
    STORAGE_KEY,
    [],
  )

  const addViewedCar = useCallback(
    <U extends CarType>(carData: CarDtoType<U>, carType: U) => {
      // console.log("[usePreviouslyViewed] addViewedCar called", {
      //   carType,
      //   carData,
      //   brandsCount: brands?.length,
      // })

      let transformedCar: PreviouslyViewedCar

      if (carType === "Sale") {
        transformedCar = transformCarDataForPreviouslyViewed({
          carData: carData as SaleCarDto,
          brands,
        })
        // console.log(
        //   "[usePreviouslyViewed] Sale car transformed:",
        //   transformedCar,
        // )
      } else {
        transformedCar = transformLeasingCarDataForPreviouslyViewed({
          carData: carData as LeasingCarDto,
          brands,
        })
        // console.log(
        //   "[usePreviouslyViewed] Leasing car transformed:",
        //   transformedCar,
        // )
      }

      setViewedCars((prevCars) => {
        // console.log("[usePreviouslyViewed] Current cars before update:", {
        //   count: prevCars.length,
        //   cars: prevCars,
        // })

        // Remove the car if it already exists (to avoid duplicates)
        // For sale cars: match by vehicleNumber, for leasing cars: match by identifier
        const filteredCars = prevCars.filter(
          (car) => car.identifier !== transformedCar.identifier,
        )

        // console.log("[usePreviouslyViewed] After filtering duplicates:", {
        //   beforeCount: prevCars.length,
        //   afterCount: filteredCars.length,
        //   removedDuplicate: prevCars.length !== filteredCars.length,
        //   newCarIdentifier: transformedCar.identifier,
        // })

        // Add the new car at the beginning
        const updatedCars = [transformedCar, ...filteredCars]

        // console.log("[usePreviouslyViewed] After adding new car:", {
        //   count: updatedCars.length,
        // })

        // Keep only the most recent MAX_CARS
        const finalCars = updatedCars.slice(0, MAX_CARS)

        // console.log("[usePreviouslyViewed] Final cars after slice:", {
        //   count: finalCars.length,
        //   maxCars: MAX_CARS,
        //   cars: finalCars,
        // })

        return finalCars
      })
    },
    [setViewedCars, brands],
  )

  const removeViewedCar = useCallback(
    (identifier: string | number) => {
      setViewedCars((prevCars) =>
        prevCars.filter((car) => car.identifier !== identifier),
      )
    },
    [setViewedCars],
  )

  const clearViewedCars = useCallback(() => {
    setViewedCars([])
  }, [setViewedCars])

  const isCarViewed = useCallback(
    (identifier: string | number) => {
      return viewedCars.some((car) => car.identifier === identifier)
    },
    [viewedCars],
  )

  // Helper function to check if a sale car is viewed by vehicleNumber
  const isSaleCarViewed = useCallback(
    (vehicleNumber: number) => {
      return viewedCars.some(
        (car) => car.type === "Sale" && car.vehicleNumber === vehicleNumber,
      )
    },
    [viewedCars],
  )

  // Helper function to check if a leasing car is viewed by manufacturerCode and modelCode
  const isLeasingCarViewed = useCallback(
    (manufacturerCode: number, modelCode: number) => {
      return viewedCars.some(
        (car) =>
          car.type === "Leasing" &&
          car.manufacturerCode === manufacturerCode &&
          car.modelCode === modelCode,
      )
    },
    [viewedCars],
  )

  return {
    viewedCars,
    addViewedCar,
    removeViewedCar,
    clearViewedCars,
    isCarViewed,
    isSaleCarViewed,
    isLeasingCarViewed,
  }
}
